import { DatePipe, JsonPipe, Location } from '@angular/common';
import { Component, Input, OnInit, ViewChild, WritableSignal, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NavigationComponent } from '../../../components/navigation/navigation.component';
import { AgeStringPipe } from '../../../pipes/age-string.pipe';
import { AssessmentNotesPipe } from '../../../pipes/assessment-notes.pipe';
import { AssessmentScorePipe } from '../../../pipes/assessment-score.pipe';
import { DecadePipe } from '../../../pipes/decade.pipe';
import { GenderStringPipe } from '../../../pipes/gender-string.pipe';
import { MonthStringPipe } from '../../../pipes/month-string.pipe';
import { AssessmentsService } from '../../../services/assessments.service';
import { LogService } from '../../../services/log.service';
import { Assessment, Patient, ReportItem } from '../../../services/model';
import { OfficeService } from '../../../services/office.service';
import { PatientsService } from '../../../services/patients.service';
import { GraphNewComponent } from '../graph-new/graph-new.component';
import { GraphComponent } from '../graph/graph.component';
import { ReportTableComponent } from '../report/report-table/report-table.component';
import { AssessmentTableComponent } from './assessment-table/assessment-table.component';

@Component({
  selector: 'app-report',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    JsonPipe,
    MatTableModule,
    MonthStringPipe,
    GenderStringPipe,
    AgeStringPipe,
    DatePipe,
    DecadePipe,
    AssessmentNotesPipe,
    AssessmentScorePipe,
    MatIconModule,
    MatProgressBarModule,
    RouterModule,
    MatMenuModule,
    MatTabsModule,
    AssessmentTableComponent,
    ReportTableComponent,
    GraphComponent,
    GraphNewComponent,
    NavigationComponent,
  ],
  templateUrl: './assessments.component.html',
  styleUrl: './assessments.component.scss',
})
export class AssessmentsComponent implements OnInit {
  private location: Location = inject(Location);
  @ViewChild('reportcomponent') reportComponent!: ReportTableComponent;
  @ViewChild('mattab') mattab!: MatTabGroup;
  @Input() patientId?: string;
  patient: WritableSignal<Patient | undefined> = signal(undefined);
  isPrinting = signal<boolean>(false);
  tabSelected = signal<number>(0);
  isFullPatientDetailsVisibleEnabled: boolean = false;
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private assessmentsService: AssessmentsService = inject(AssessmentsService);
  private patientsService: PatientsService = inject(PatientsService);
  private officeService: OfficeService = inject(OfficeService);
  private logger: LogService = inject(LogService);

  assessmentList: WritableSignal<Assessment[]> = this.assessmentsService.assessmentList;
  reportList: WritableSignal<ReportItem[] | undefined> = this.assessmentsService.reportList;

  tableColumns = ['date', 'type', 'score', 'note', 'buttons'];

  $routeParams = toSignal(this.route.queryParamMap);

  constructor() {
    // effect(() => {
    //   console.log('effect-assessmentlist() ', this.assessmentList());
    // });
    const effectRef = effect(
      () => {
        const office = this.officeService.office();

        if (office) {
          this.isFullPatientDetailsVisibleEnabled =
            this.officeService.office()?.isFullPatientDetailsVisibleEnabled ?? false;
          effectRef.destroy();
        }
      },
      { manualCleanup: true }
    );
  }

  ngOnInit(): void {
    // console.log('patientID', this.patientId);
    if (this.patientId !== undefined) {
      this.patientsService.getPatient(this.patientId).then(patient => {
        this.logger.log('patient assessments ' + patient._documentId);
        this.patient.set(patient);
        this.assessmentsService.getAssessments();
      });
    }

    const tab: any = this.$routeParams()?.get('tab');
    // console.log('chris tab ' + tab + '<');

    if (tab === '1') {
      const myinterval = setInterval(() => {
        if (this.mattab !== undefined) {
          // console.log('chris tab ' + tab + '<');
          this.mattab.selectedIndex = 1;
          this.onTabChange(1);
          clearInterval(myinterval);
        }
      });
    }
  }

  newAssessment() {
    this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
  }

  navigateToAssessment(assessment: Assessment) {
    console.log('ass', assessment);
    if (assessment) {
      this.router.navigate(['../../assessments/' + assessment.type.toLowerCase() + '/' + assessment._documentId], {
        relativeTo: this.route,
      });
    }
  }

  deleteAssessment(assessment: Assessment) {
    if (assessment?._documentId) {
      this.assessmentsService.deleteAssessment(assessment._documentId);
    }
  }

  copyHrefToClipboard() {
    const currentURL = window.location.href;
    return new Promise((resolve, reject) => {
      if (
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard !== 'undefined' &&
        navigator.permissions !== undefined
      ) {
        const type = 'text/plain';
        const blob = new Blob([currentURL], { type });
        const data = [new ClipboardItem({ [type]: blob })];
        navigator.permissions.query({ name: 'clipboard-write' as PermissionName }).then(permission => {
          if (permission.state === 'granted' || permission.state === 'prompt') {
            navigator.clipboard.write(data).then(resolve, reject).catch(reject);
            this.snackBar.open('Link zu dem Patienten wurde erfolgreich in die Zwischenablage kopiert', '', {
              duration: 3000,
            });
          } else {
            this.snackBar.open(
              'Link konnte wegen mangelnder Geräte-Berechtigung nicht kopiert werden. Bitte manuell über die Browser-Leiste kopieren',
              '',
              {
                duration: 10000,
              }
            );
            reject(new Error('Permission not granted!'));
          }
        });
      }
    });
  }
  onTabChange(selectedTabId: number): void {
    this.tabSelected.set(selectedTabId);
    if (selectedTabId === 1) {
      this.changeUrlParam('tab', '1');
    } else {
      this.changeUrlParam('tab');
    }
  }

  onIsPrintingChanged($event: boolean) {
    this.isPrinting.set($event);
    // throw new Error('Method not implemented.');
  }

  changeUrlParam(paramKey: string, value?: string): void {
    // Aktuelle URL und Parameter abrufen
    const url = new URL(window.location.href);
    // const search = this.router.url.split('?')[0]; // Nur den Pfad ohne bestehende Query-Parameter
    const params = new URLSearchParams(url.search);
    // console.log('chris params', { url, params: params.toString() });

    if (value === undefined) {
      // Parameter entfernen
      params.delete(paramKey);
    } else {
      params.set(paramKey, value);
    }

    // Aktualisierte URL setzen
    const newUrl = `${url.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    this.location.replaceState(newUrl);
  }

  onChangeFullPatientDetailsVisibleEnabled() {
    const office = this.officeService.office()!;
    this.isFullPatientDetailsVisibleEnabled = !this.isFullPatientDetailsVisibleEnabled;
    office.isFullPatientDetailsVisibleEnabled = this.isFullPatientDetailsVisibleEnabled;
    this.officeService.updateOffice(office);
  }
}
